import type { TokenCredential } from "@azure/identity";
import { select } from "@inquirer/prompts";
import { authedJsonPagedValues } from "./http.js";
import { LocalCliError } from "./errors.js";

const ARM = "https://management.azure.com";
const ARM_SCOPE = "https://management.azure.com/.default";
const ACCOUNTS_API = "2024-10-01";
const PROJECTS_API = "2025-04-01-preview";

export interface FoundryProject {
  accountName: string;
  accountScope: string; // ARM resource id of the account (the RBAC grant scope)
  projectName: string;
  region: string;
  endpoint: string; // data-plane: https://<account>.services.ai.azure.com/api/projects/<project>
  projectPrincipalId: string | null; // the project MI (for the AcrPull check)
}

interface AccountEntry {
  name?: string;
  kind?: string;
  location?: string;
  id?: string;
}
interface ProjectEntry {
  name?: string;
  identity?: { principalId?: string };
  id?: string;
}

async function listProjectsForAccount(
  credential: TokenCredential,
  accountId: string,
): Promise<ProjectEntry[]> {
  const url = `${ARM}${accountId}/projects?api-version=${PROJECTS_API}`;
  return await authedJsonPagedValues<ProjectEntry>({ credential, scope: ARM_SCOPE, url });
}

/** Format a project candidate for display — includes account so identically-named projects are distinguishable. */
export function formatProjectCandidate(c: FoundryProject): string {
  return `${c.projectName} (account=${c.accountName}, ${c.region})`;
}

/**
 * The resource-group segment of an ARM resource id, lowercased for comparison
 * (Azure resource-group names are case-insensitive and ARM does not echo the
 * caller's casing).
 *
 * Returns undefined when the id carries no `resourceGroups` segment. Callers
 * MUST treat that as "cannot be placed" and exclude the resource — never as a
 * match. See the scoping block in resolveFoundryProject.
 */
function resourceGroupOfArmId(id: string): string | undefined {
  return /\/resourceGroups\/([^/]+)/i.exec(id)?.[1].toLowerCase();
}

/**
 * Discover the target Foundry project by enumerating AIServices accounts and
 * their projects in the subscription: single → use it; multiple → interactive
 * select or a FOUNDRY_PROJECT_MULTIPLE error. When `endpoint` is given it is
 * matched against the discovered candidates (it does NOT skip enumeration —
 * an earlier version of this comment claimed it did, which made the
 * FOUNDRY_PROJECT_ENDPOINT_NOT_FOUND path very confusing to debug).
 *
 * The enumeration MUST be paginated. ARM splits list results with `nextLink`
 * and may return EMPTY early pages that carry only a continuation token; a
 * single-page read then finds zero accounts and every endpoint is rejected —
 * including a project created seconds earlier. See authedJsonPagedValues.
 *
 * `resourceGroup` narrows DISCOVERY to one resource group. Callers that know
 * which group they own (bootstrap state names it) should pass it: without it,
 * an unrelated Foundry project elsewhere in the subscription manufactures an
 * ambiguity the caller could already have answered. Same rationale as
 * discoverGateway in bootstrap-finalize.ts.
 */
export async function resolveFoundryProject(opts: {
  credential: TokenCredential;
  subscriptionId: string;
  interactive: boolean;
  endpoint?: string;
  agentEndpoint?: string; // when supplied, auto-picks the matching candidate — skips interactive prompt
  /** Narrow discovery to this resource group. Explicit endpoint pointers bypass it. */
  resourceGroup?: string;
  /** Reports what `resourceGroup` excluded. Called ONLY when something was excluded. */
  onScopedOut?: (dropped: FoundryProject[]) => void;
}): Promise<FoundryProject> {
  const candidates: FoundryProject[] = [];

  const accounts = await authedJsonPagedValues<AccountEntry>({
    credential: opts.credential,
    scope: ARM_SCOPE,
    url: `${ARM}/subscriptions/${opts.subscriptionId}/providers/Microsoft.CognitiveServices/accounts?api-version=${ACCOUNTS_API}`,
  });

  for (const acc of accounts) {
    if (acc.kind !== "AIServices" || !acc.name || !acc.id) continue;
    const projects = await listProjectsForAccount(opts.credential, acc.id);
    for (const proj of projects) {
      if (!proj.name) continue;
      // ARM returns the project's qualified name "<account>/<project>"; the
      // data-plane endpoint wants only the bare project segment.
      const projectName = proj.name.includes("/")
        ? proj.name.slice(proj.name.lastIndexOf("/") + 1)
        : proj.name;
      candidates.push({
        accountName: acc.name,
        accountScope: acc.id,
        projectName,
        region: acc.location ?? "",
        endpoint: `https://${acc.name}.services.ai.azure.com/api/projects/${projectName}`,
        projectPrincipalId: proj.identity?.principalId ?? null,
      });
    }
  }

  // Explicit --endpoint: match a discovered candidate, else fail clearly.
  if (opts.endpoint) {
    const match = candidates.find((c) => c.endpoint === opts.endpoint);
    if (match) return match;
    throw new LocalCliError({
      code: "FOUNDRY_PROJECT_ENDPOINT_NOT_FOUND",
      message: `--endpoint '${opts.endpoint}' did not match any discovered Foundry project in subscription ${opts.subscriptionId}.`,
      hint: `Discovered: ${candidates.map((c) => c.endpoint).join(", ") || "(none)"}.`,
    });
  }

  // When the caller supplies the agent's projectEndpoint (read from
  // per-agent yaml), auto-pick the candidate that matches. Skips the
  // interactive prompt + the FOUNDRY_PROJECT_MULTIPLE error for operators
  // with > 1 Foundry project in their subscription.
  //
  // Precedence ladder:
  //   1. opts.endpoint (explicit --endpoint flag) — handled above, always wins.
  //   2. opts.agentEndpoint (from yaml) AND ∈ candidates → use it.
  //   3. opts.resourceGroup narrows the remaining set (DISCOVERY only).
  //   4. 0 candidates → error.
  //   5. 1 candidate → use it.
  //   6. > 1 candidates → interactive prompt (TTY) or FOUNDRY_PROJECT_MULTIPLE error.
  //
  // Steps 1-2 deliberately match against the UNFILTERED set: both are explicit
  // pointers at a known project, and --endpoint is the documented way to reach
  // one OUTSIDE the scoped group. Scoping them away would break the remedy that
  // every refusal and timeout message here points at.
  //
  // When agentEndpoint is set but doesn't match any candidate, fall through
  // to step 4/5/6 — the yaml points at a project that no longer exists in
  // this subscription; the operator gets a clean error or interactive pick.
  if (opts.agentEndpoint) {
    const match = candidates.find((c) => c.endpoint === opts.agentEndpoint);
    if (match) return match;
  }

  // Step 3. There is deliberately NO subscription-wide fallback when the group
  // holds nothing: falling back is the defect this scoping exists to fix — it
  // can resolve someone else's production project. Zero here stays zero, which
  // is also what lets a caller polling for a not-yet-created project keep
  // waiting instead of binding to a stranger.
  let scoped = candidates;
  if (opts.resourceGroup) {
    const want = opts.resourceGroup.toLowerCase();
    // Fails CLOSED: an account whose ARM id carries no resource-group segment
    // cannot be PROVEN to live here, so it is excluded rather than kept.
    scoped = candidates.filter((c) => resourceGroupOfArmId(c.accountScope) === want);
    const dropped = candidates.filter((c) => !scoped.includes(c));
    if (dropped.length > 0) opts.onScopedOut?.(dropped);
  }

  // Where we looked — naming the resource group matters: an empty result inside
  // one group otherwise reads as "nothing exists anywhere", which is the wrong
  // conclusion when the subscription is full of other projects.
  const searched = opts.resourceGroup
    ? `resource group ${opts.resourceGroup} (subscription ${opts.subscriptionId})`
    : `subscription ${opts.subscriptionId}`;

  if (scoped.length === 0) {
    throw new LocalCliError({
      code: "FOUNDRY_PROJECT_ZERO",
      message: `No AIServices Foundry project found in ${searched}.`,
      hint: "Create one with 'm8t foundry create --resource-group <rg> --location <region>', switch subscription with 'az account set --subscription <id>', or pass --endpoint.",
    });
  }
  if (scoped.length === 1) return scoped[0];

  if (!opts.interactive) {
    throw new LocalCliError({
      code: "FOUNDRY_PROJECT_MULTIPLE",
      message: opts.resourceGroup
        ? `Resource group ${opts.resourceGroup} holds more than one Foundry project: ${scoped.map(formatProjectCandidate).join("; ")}.`
        : `Multiple Foundry projects found: ${scoped.map(formatProjectCandidate).join("; ")}.`,
      hint: `Pass --endpoint <url> to choose. Endpoints: ${scoped.map((c) => c.endpoint).join(" | ")}`,
    });
  }

  const endpoint = await select({
    message: `Multiple Foundry projects in ${searched}. Choose one:`,
    choices: scoped.map((c) => ({
      name: formatProjectCandidate(c),
      value: c.endpoint,
    })),
  });
  const chosen = scoped.find((c) => c.endpoint === endpoint);
  if (!chosen) {
    throw new LocalCliError({ code: "FOUNDRY_PROJECT_SELECTION_FAILED", message: "No project selected." });
  }
  return chosen;
}
